import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Typography,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Avatar,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getTrackColor } from '../../../utils/mortgageUtils';

const MortgageTrack = ({ 
  track, 
  index, 
  trackTypes, 
  handleTrackChange, 
  removeTrack,
  canDelete 
}) => {
  const trackType = trackTypes.find(t => t.id === parseInt(track.type));
  const trackColor = getTrackColor(track.type || "default");

  return (
    <Card
      elevation={2}
      sx={{
        mb: 3,
        borderRadius: '10px',
        border: `1px solid ${trackColor.border || '#e0e0e0'}`,
        borderTop: `4px solid ${trackColor.main || '#1976d2'}`,
        overflow: 'visible',
        position: 'relative'
      }}
    >
      <CardHeader
        avatar={
          <Avatar 
            sx={{ 
              bgcolor: trackColor.main || '#1976d2',
              width: 30,
              height: 30,
              fontSize: '0.9rem',
              fontWeight: 'bold',
              marginLeft: 1
            }}
          >
            {index + 1}
          </Avatar>
        }
        title={
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            direction: 'rtl'
          }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mr: 1 }}>
              {trackType?.name || 'מסלול חדש'}
            </Typography>
            {track.amount && (
              <Chip 
                label={`${Number(track.amount).toLocaleString()} ₪`} 
                size="small" 
                variant="outlined" 
                sx={{ minWidth: 'fit-content' }} 
              />
            )}
          </Box>
        }
        action={
          canDelete && (
            <IconButton 
              color="error" 
              onClick={() => removeTrack(index)}
              size="small"
              sx={{ mr: 1 }}
            >
              <DeleteIcon />
            </IconButton>
          )
        }
      />
      
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 0.8 }}>
                סוג מסלול
              </Typography>
              <FormControl fullWidth variant="outlined" sx={{ bgcolor: 'white' }}>
                <Select
                  value={track.type}
                  onChange={(e) => handleTrackChange(index, 'type', e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <em>בחר סוג מסלול</em>;
                    }
                    const selectedType = trackTypes.find(t => t.id === parseInt(selected));
                    return selectedType?.name || '';
                  }}
                  sx={{ 
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: trackColor.border || 'rgba(0, 0, 0, 0.23)'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: trackColor.main || '#1976d2'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: trackColor.main || '#1976d2'
                    }
                  }}
                >
                  <MenuItem value=""><em>בחר סוג</em></MenuItem>
                  {trackTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 0.8 }}>
                סכום הלוואה
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={track.amount}
                onChange={(e) => handleTrackChange(index, 'amount', e.target.value)}
                variant="outlined"
                placeholder="הזן סכום"
                sx={{ bgcolor: 'white' }}
                InputProps={{ 
                  style: { textAlign: "right" },
                  endAdornment: <Typography>₪</Typography>
                }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ 
              p: 2, 
              bgcolor: trackColor.light || '#f5f5f5',
              borderRadius: '8px',
              border: `1px solid ${trackColor.border || '#e0e0e0'}`
            }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 0.8, display: 'flex', alignItems: 'center' }}>
                    ריבית בסיס
                    <Tooltip title="ריבית הבסיס של המסלול (קבועה או משתנה)" placement="top">
                      <InfoOutlinedIcon sx={{ fontSize: '0.8rem', ml: 0.8, verticalAlign: 'middle', opacity: 0.7 }} />
                    </Tooltip>
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={track.current_base_interest || ""}
                    onChange={(e) => handleTrackChange(index, 'current_base_interest', e.target.value)}
                    variant="outlined"
                    placeholder="ריבית בסיס"
                    sx={{ bgcolor: 'white' }}
                    InputProps={{ 
                      style: { textAlign: "right" },
                      endAdornment: <Typography>%</Typography>
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 0.8, display: 'flex', alignItems: 'center' }}>
                    מרווח
                    <Tooltip title="המרווח מריבית הבסיס (התוספת הספציפית שלך)" placement="top">
                      <InfoOutlinedIcon sx={{ fontSize: '0.8rem', ml: 0.8, verticalAlign: 'middle', opacity: 0.7 }} />
                    </Tooltip>
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={track.addition_rate || ""}
                    onChange={(e) => handleTrackChange(index, 'addition_rate', e.target.value)}
                    variant="outlined"
                    placeholder="מרווח"
                    sx={{ bgcolor: 'white' }}
                    InputProps={{ 
                      style: { textAlign: "right" },
                      endAdornment: <Typography>%</Typography>
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 0.8, display: 'flex', alignItems: 'center' }}>
                    ריבית כוללת
                    <Tooltip title="הריבית הכוללת (בסיס + מרווח)" placement="top">
                      <InfoOutlinedIcon sx={{ fontSize: '0.8rem', ml: 0.8, verticalAlign: 'middle', opacity: 0.7 }} />
                    </Tooltip>
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={track.interest}
                    onChange={(e) => handleTrackChange(index, 'interest', e.target.value)}
                    variant="outlined"
                    placeholder="ריבית כוללת"
                    sx={{ 
                      bgcolor: 'white',
                      '& .MuiOutlinedInput-root': {
                        fontWeight: 'bold'
                      },
                      '& .MuiInputBase-input': {
                        color: trackColor.main || '#1976d2'
                      }
                    }}
                    InputProps={{ 
                      style: { textAlign: "right" },
                      endAdornment: <Typography>%</Typography>
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 0.8 }}>
                תקופה (בשנים)
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={track.period}
                onChange={(e) => handleTrackChange(index, 'period', e.target.value)}
                variant="outlined"
                placeholder="מספר שנים"
                sx={{ bgcolor: 'white' }}
                InputProps={{ 
                  style: { textAlign: "right" }
                }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 0.8 }}>
                החזר חודשי
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={track.monthly_payment || ""}
                onChange={(e) => handleTrackChange(index, 'monthly_payment', e.target.value)}
                variant="outlined"
                placeholder="החזר חודשי"
                sx={{ bgcolor: 'white' }}
                InputProps={{ 
                  style: { textAlign: "right" },
                  endAdornment: <Typography>₪</Typography>
                }}
                helperText={!track.monthly_payment && track.amount && track.interest && track.period ? 
                  "יחושב אוטומטית אם תשאיר ריק" : ""}
              />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default MortgageTrack; 